// src/hooks/useTeamMembers.js

import { useState, useEffect, useRef } from 'react';
import { db } from '../firebase';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  getCountFromServer
} from 'firebase/firestore';
import { getAllAssistants } from '../services/openaiService';

/**
 * Hook para obtener:
 * - Miembros reales MKT (documentos en users con accessCode == userData.accessCode)
 * - Miembros ficticios del usuario actual (userData.equipo)
 * - Contar las conversaciones de cada miembro si estamos en /messages
 */
const useTeamMembers = (currentUser, isMessagesPage) => {
  const [teamMembers, setTeamMembers] = useState([]);          // Reales + Ficticios
  const [teamMembersWithCounts, setTeamMembersWithCounts] = useState([]); // Para mostrar conteo
  const countsRunIdRef = useRef(0);

  useEffect(() => {
    if (!currentUser) return;

    const fetchTeamMembers = async () => {
      try {
        // 1) Obtener datos del usuario actual
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userDocRef);

        if (!userSnap.exists()) {
          console.error('No se encontró el documento del usuario actual.');
          return;
        }
        const userData = userSnap.data();

        // 2) Miembros ficticios => SOLO del array "equipo" del usuario actual
        const fictitiousMembers = Array.isArray(userData.equipo)
          ? userData.equipo
          : [];

        // 3) Miembros reales => todos los docs en "users" con el mismo accessCode,
        //    PERO sin mezclar su propio array "equipo" (solo datos del doc).
        let realMktMembers = [];
        const { accessCode } = userData;
        if (accessCode) {
          const usersRef = collection(db, 'users');
          const q = query(usersRef, where('accessCode', '==', accessCode));
          const snapshot = await getDocs(q);

          realMktMembers = snapshot.docs.map((docSnap) => {
            const d = docSnap.data();
            return {
              id: docSnap.id,
              type: 'real',
              nombre: d.name || '',       // o d.nombre, según guardes
              apellido: d.apellido || '',
              cargo: d.cargo || '',
            };
          });
        }

        // 4) Mapear los ficticios del usuario actual => IDs "member0", "member1", etc.
        const mappedFict = fictitiousMembers.map((fict, index) => ({
          id: `member${index}`,
          type: 'fictitious',
          nombre: fict.nombre,
          apellido: fict.apellido,
          cargo: fict.cargo || '',
        }));

        // 5) Unir real + ficticios en un solo array
        const combined = [...realMktMembers, ...mappedFict];
        setTeamMembers(combined);

      } catch (error) {
        console.error('Error al obtener miembros del equipo:', error);
      }
    };

    fetchTeamMembers();
  }, [currentUser]);

  // ==========================================
  //    Efecto para contar conversaciones (si /messages)
  // ==========================================
  useEffect(() => {
    if (!isMessagesPage || teamMembers.length === 0 || !currentUser) {
      setTeamMembersWithCounts([]);
      return;
    }

    const ownerNameFor = (m) => `${m.nombre} ${m.apellido}`.trim();
    const runId = ++countsRunIdRef.current;

    let cancelled = false;

    // Inicializa en 0 para evitar UI vacía mientras calcula
    setTeamMembersWithCounts(
      teamMembers.map((m) => ({
        ...m,
        conversationCount: 0,
      }))
    );

    const fetchConversationCounts = async () => {
      try {
        // Obtener todos los asistentes del usuario actual
        const assistantsData = await getAllAssistants(currentUser.uid);

        const countPromises = [];
        for (const assistant of assistantsData) {
          const threadsCollectionRef = collection(db, `threads_${assistant.id}`);

          for (const member of teamMembers) {
            const ownerName = ownerNameFor(member);
            // Variantes que podrían estar en la DB (por espacios, etc.)
            const ownerVariants = [ownerName, `${ownerName} `];

            const q = query(
              threadsCollectionRef,
              where('conversationowner', 'in', ownerVariants)
            );

            countPromises.push(
              getCountFromServer(q)
                .then((agg) => ({
                  ownerName,
                  count: agg.data().count || 0,
                }))
                .catch((error) => {
                  console.error(`Error contando threads_${assistant.id}:`, error);
                  return { ownerName, count: 0 };
                })
            );
          }
        }

        const results = await Promise.all(countPromises);

        if (cancelled || countsRunIdRef.current !== runId) return;

        const combinedCounts = {};
        results.forEach(({ ownerName, count }) => {
          combinedCounts[ownerName] = (combinedCounts[ownerName] || 0) + (count || 0);
        });

        const membersWithCounts = teamMembers.map((m) => {
          const oName = ownerNameFor(m);
          return {
            ...m,
            conversationCount: combinedCounts[oName] || 0,
          };
        });

        setTeamMembersWithCounts(membersWithCounts);
      } catch (error) {
        console.error('Error en fetchConversationCounts:', error);
      }
    };

    fetchConversationCounts();

    return () => {
      cancelled = true;
    };
  }, [isMessagesPage, teamMembers, currentUser]);

  // Devolvemos 2 arrays: sin counts (teamMembers) y con counts (teamMembersWithCounts)
  return { teamMembers, teamMembersWithCounts };
};

export default useTeamMembers;
